import React, { memo, useCallback, useRef, useState } from "react";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import { getModelThumbnail } from "domains/models/utils";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Button from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useHandleApiError } from "infra/api/error";
import {
  useGetModelsByModelIdQuery,
  usePutModelsByModelIdMutation,
} from "infra/api/generated/api";

import {
  Box,
  Center,
  Flex,
  FormControl,
  HStack,
  Image as ChakraImage,
  Skeleton,
  SkeletonText,
  Slider,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";

const IMAGE_SIZE = 40;
const CONCEPT_SLIDER_MIN = 0;
const CONCEPT_SLIDER_MAX = 1;

export default function SidebarSectionConcepts() {
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { selectedProject } = useTeamContext();
  const { model, setModel, concepts, setConcepts, isInCanvas } =
    useInferenceCreateContext();
  const [updateModelTrigger, { isLoading: isUpdateModelLoading }] =
    usePutModelsByModelIdMutation();

  const isModelOwned = model?.ownerId === selectedProject.id;
  const [isLocked, setIsLocked] = useState(false);

  // ----------------------------------

  const handleConceptChange = useCallback(
    (modelId: string, newScale: number) => {
      setConcepts((prev) =>
        prev?.map((concept) =>
          concept.modelId === modelId
            ? { ...concept, scale: newScale }
            : concept
        )
      );
    },
    [setConcepts]
  );

  const handleResetConcepts = useCallback(() => {
    setConcepts(model?.concepts);
  }, [model?.concepts, setConcepts]);

  const handleSaveConceptsValues = useCallback(async () => {
    if (!model?.id) return;
    try {
      const { model: newModel } = await updateModelTrigger({
        projectId: selectedProject.id,
        modelId: model.id,
        body: {
          concepts: concepts,
        },
      }).unwrap();
      setModel(newModel);
      successToast({
        title: "Settings successfully saved",
      });
    } catch (error) {
      handleApiError(error, "There was an error saving the settings");
    }
  }, [
    concepts,
    model?.id,
    handleApiError,
    selectedProject.id,
    updateModelTrigger,
    successToast,
    setModel,
  ]);

  return (
    <SidebarSection
      compact={isInCanvas}
      headerProps={{
        id: "concepts",
        title: "Model Components",
        topRight: !model ? (
          <Spinner w="14px" h="14px" />
        ) : (
          <HStack spacing={2}>
            {isModelOwned && (
              <Tooltip
                label={isLocked ? "Unlock and customize" : "Save settings"}
              >
                <ButtonSectionTopIcon
                  onClick={() => {
                    if (isLocked) {
                      setIsLocked(!isLocked);
                    } else {
                      void handleSaveConceptsValues();
                    }
                  }}
                  isLoading={isUpdateModelLoading}
                  data-testid={`inference-builder-concept-${
                    isLocked ? "unlock" : "save"
                  }-button`}
                >
                  <Icon
                    id={isLocked ? "Layout/Settings" : "Ui/Save"}
                    h="14px"
                  />
                </ButtonSectionTopIcon>
              </Tooltip>
            )}
            {isModelOwned && (
              <ButtonSectionTopIcon
                onClick={handleResetConcepts}
                data-testid="inference-builder-concept-reset-button"
              >
                <Icon id="Ui/Refresh" h="14px" />
              </ButtonSectionTopIcon>
            )}
          </HStack>
        ),
      }}
    >
      <VStack align="stretch" spacing={4}>
        {concepts?.map(
          ({ modelId, scale }: { modelId: string; scale: number }) => (
            <ConceptItem
              key={modelId}
              modelId={modelId}
              currentScale={scale}
              defaultScale={
                model?.concepts?.find((c) => c.modelId === modelId)?.scale ??
                0.5
              }
              onScaleChange={handleConceptChange}
              isDisabled={!isModelOwned || isLocked}
            />
          )
        )}
      </VStack>
    </SidebarSection>
  );
}

// ------------------------------------

export const ConceptItem = memo(function ConceptSlider({
  modelId,
  defaultScale,
  currentScale,
  onScaleChange,
  onRemoveConcept,
  isDisabled,
}: {
  modelId: string;
  currentScale: number;
  defaultScale?: number;
  onScaleChange?: (modelId: string, newScale: number) => void;
  onRemoveConcept?: (modelId: string) => void;
  isDisabled?: boolean;
}) {
  const { selectedProject } = useTeamContext();
  const { data: modelData, isLoading: isModelDataLoading } =
    useGetModelsByModelIdQuery({
      projectId: selectedProject.id,
      modelId,
    });

  const handleConceptChange = useCallback(
    (value: number) => {
      if (onScaleChange) {
        onScaleChange(modelId, value);
      }
    },
    [modelId, onScaleChange]
  );

  const model = modelData?.model;

  return (
    <FormControl
      as={Flex}
      gap={2}
      w="full"
      color="textSecondary"
      _hover={{
        color: "textPrimary",
      }}
    >
      {isModelDataLoading ? (
        <Skeleton w={`${IMAGE_SIZE}px`} h={`${IMAGE_SIZE}px`} />
      ) : (
        <ChakraImage
          w={`${IMAGE_SIZE}px`}
          h={`${IMAGE_SIZE}px`}
          aspectRatio="1"
          fit="cover"
          rounded="md"
          src={
            getModelThumbnail(model, {
              width: 64,
            }).url
          }
        />
      )}
      <VStack align="space-between" flex={1} overflow="hidden" spacing={0}>
        <Flex justify="space-between" gap="2">
          {isModelDataLoading ? (
            <SkeletonText w="100px" noOfLines={1} skeletonHeight="20px" />
          ) : (
            <Text color="inherit" isTruncated size="body.md">
              {model?.name}
            </Text>
          )}
          <Text color="inherit" size="body.md">
            {currentScale.toFixed(2)}
          </Text>
        </Flex>

        <ConceptItemSlider
          max={CONCEPT_SLIDER_MAX}
          min={CONCEPT_SLIDER_MIN}
          step={0.05}
          value={currentScale}
          defaultValue={defaultScale}
          onChange={handleConceptChange}
          isDisabled={isDisabled}
        />
      </VStack>
      {onRemoveConcept && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onRemoveConcept(modelId)}
          colorScheme="secondary"
        >
          <Icon id="Ui/MinusSm" color="textSecondary" />
        </Button>
      )}
    </FormControl>
  );
});

// ------------------------------------

interface ConceptItemSliderProps {
  min: number;
  max: number;
  value: number;
  defaultValue?: number;
  step: number;
  onChange: (value: number) => void;
  /* withWarning: boolean; */
  isDisabled?: boolean;
}

function ConceptItemSlider({
  min,
  max,
  value,
  defaultValue = 0,
  step,
  onChange,
  /* withWarning, */
  isDisabled,
}: ConceptItemSliderProps) {
  const thumbRef = useRef<HTMLDivElement>(null);

  const trackColor = useColorModeValue("gray.200", "gray.700");
  const filledColor = useColorModeValue("whiteAlpha.500", "whiteAlpha.300");
  const _warningColor = useColorModeValue("warning.500", "warning.300");

  const width = Math.abs(
    calculateRelativePositionPercentage(min, max, value) -
      calculateRelativePositionPercentage(min, max, defaultValue)
  );

  let left;
  if (value < defaultValue) {
    left = calculateRelativePositionPercentage(min, max, value);
  } else {
    left = calculateRelativePositionPercentage(min, max, defaultValue);
  }
  return (
    <Center pt={2}>
      <Slider
        w="100%"
        colorScheme="whiteAlpha"
        focusThumbOnChange={false}
        isDisabled={isDisabled}
        max={max}
        min={min}
        onChange={onChange}
        onChangeStart={() => {
          thumbRef.current?.focus();
        }}
        step={step}
        value={value}
      >
        <SliderTrack bg={trackColor}>
          <Box
            pos="absolute"
            left={`${left}%`}
            w={`${width}%`}
            h="100%"
            bg={isDisabled ? "gray.500" : filledColor}
          />
        </SliderTrack>
        <SliderThumb
          ref={thumbRef}
          // border={isDisabled ? "none" : "blue.300"}
        />
      </Slider>
    </Center>
  );
}

// ------------------------------------

function calculateRelativePositionPercentage(
  min: number,
  max: number,
  currentValue: number
): number {
  const percentage = ((currentValue - min) / (max - min)) * 100;

  return Math.min(Math.max(percentage, 0), 100);
}
